/**
 * Homepage 2026 — the static link registry.
 *
 * Every entry names a destination by KIND + SLUG, never by a typed path:
 *   collection → /collections/<slug>, rendered only if the slug is active + indexed
 *   hub        → /<slug>,             rendered only if the hub is active
 *   route      → an app route that exists in app/ (verified 09/02/2026)
 *   search     → /search?q=…          (registry entries the owner has ruled may search)
 * `resolveLink` (registry entries) and `resolveHref` (literal canvas hrefs)
 * apply the gates at render time, so a gated collection or a retired hub
 * drops out — the page never renders a dead link, and since FRIDGE-798 it
 * never rewrites one into a search either: /search is noindex by design, and
 * a homepage anchor to a noindex page tells an engine "important" and "do not
 * index" in one breath. A dropped tile is logged once per process (warnGated)
 * so the missing page is a visible defect to fix at the source.
 *
 * Provenance: every slug below was matched against Supabase on 09/02/2026
 * (collections: active + indexed; hub_pages: active; tools: implemented) and
 * the live site probed 200. Width chips: no width collection exists in the
 * database, so they search (owner asked; default until answered).
 */

import { CANVAS_COLLECTION_SLUGS, CANVAS_HUB_SLUGS } from '@/components/homepage-2026/canvas/links'

export type LinkKind = 'collection' | 'hub' | 'route' | 'search'

export interface HomeLink {
  kind: LinkKind
  slug: string
  label: string
  /** optional second line / description */
  sub?: string
  /** icon key for tile/pill renderers */
  icon?: string
  /** image basename for tiles that carry a product photo (served via product-images proxy) */
  image?: string
}

export interface ResolvedLink extends HomeLink {
  href: string
}

export const HERO_CHIPS: HomeLink[] = [
  { kind: 'hub', slug: 'french-door-refrigerators', label: 'French Door' },
  { kind: 'hub', slug: 'counter-depth-refrigerators', label: 'Counter-Depth' },
  { kind: 'hub', slug: 'garage-refrigerators', label: 'Garage Ready' },
  { kind: 'collection', slug: 'refrigerators-under-1000', label: 'Under $1,000' },
  { kind: 'collection', slug: 'energy-star-refrigerators', label: 'ENERGY STAR', icon: 'leaf' },
  { kind: 'route', slug: 'rebates', label: 'Local Rebates', icon: 'pin' },
]

/** "Shop by type" — 14 tiles, each a real collection or hub, with a product photo. */
export const CATEGORY_TILES: HomeLink[] = [
  { kind: 'collection', slug: 'refrigerators', label: 'All Refrigerators' },
  { kind: 'collection', slug: 'french-door-refrigerators', label: 'French Door' },
  { kind: 'collection', slug: 'side-by-side-refrigerators', label: 'Side-by-Side' },
  { kind: 'collection', slug: 'bottom-freezer-refrigerators', label: 'Bottom Freezer' },
  { kind: 'collection', slug: 'apartment-refrigerators', label: 'Apartment Size' },
  { kind: 'collection', slug: 'mini-fridges', label: 'Mini Fridges' },
  { kind: 'collection', slug: 'upright-freezers', label: 'Upright Freezers' },
  { kind: 'collection', slug: 'chest-freezers', label: 'Chest Freezers' },
  { kind: 'collection', slug: 'wine-fridges', label: 'Wine & Beverage' },
  { kind: 'collection', slug: 'ice-makers', label: 'Ice Makers' },
  { kind: 'collection', slug: 'kegerators', label: 'Kegerators' },
  { kind: 'collection', slug: 'commercial', label: 'Commercial' },
  { kind: 'collection', slug: 'retro-fridges', label: 'Retro Fridges' },
  { kind: 'collection', slug: 'mini-freezers', label: 'Mini Freezers' },
]

/** "Shop the exact refrigerator you need" — 20 pills (icon key + label). */
export const EXACT_NEED_PILLS: HomeLink[] = [
  { kind: 'search', slug: '36 inch wide refrigerator', label: '36" wide', icon: 'ruler' },
  { kind: 'search', slug: '33 inch wide refrigerator', label: '33" wide', icon: 'ruler' },
  { kind: 'search', slug: '30 inch wide refrigerator', label: '30" wide', icon: 'ruler' },
  { kind: 'collection', slug: 'counter-depth-refrigerators', label: 'Counter-depth', icon: 'min' },
  { kind: 'collection', slug: 'garage-freezers', label: 'Garage freezers', icon: 'build' },
  { kind: 'collection', slug: 'side-by-side-refrigerators', label: 'Side-by-side', icon: 'compare' },
  { kind: 'collection', slug: 'panel-ready-freezers', label: 'Panel-ready freezers', icon: 'snow' },
  { kind: 'collection', slug: 'quiet-refrigerators', label: 'Quiet fridges', icon: 'vol' },
  { kind: 'collection', slug: 'ice-makers', label: 'Ice makers', icon: 'drop' },
  { kind: 'collection', slug: 'small-kitchen-refrigerators', label: 'Small kitchens', icon: 'home' },
  { kind: 'collection', slug: 'apartment-refrigerators', label: 'Apartment size', icon: 'home' },
  { kind: 'collection', slug: 'mini-fridges-for-dorm-room', label: 'Dorm rooms', icon: 'book' },
  { kind: 'collection', slug: 'beverage-coolers', label: 'Beverage coolers', icon: 'wine' }, // FRIDGE-1452
  { kind: 'collection', slug: 'wine-fridges', label: 'Wine fridges', icon: 'wine' },
  { kind: 'collection', slug: 'commercial', label: 'Commercial', icon: 'build' },
  { kind: 'collection', slug: 'undercounter-refrigerators', label: 'Undercounter', icon: 'min' },
  { kind: 'collection', slug: 'smart-fridges', label: 'Smart fridges', icon: 'cpu' },
  { kind: 'collection', slug: 'black-stainless-refrigerators', label: 'Black stainless', icon: 'tag' },
  { kind: 'collection', slug: 'white-refrigerators', label: 'White', icon: 'tag' },
  { kind: 'collection', slug: 'french-door-refrigerators', label: 'French door', icon: 'home' },
]

export const BUDGET_CHIPS: HomeLink[] = [
  { kind: 'collection', slug: 'refrigerators-under-500', label: 'Under $500' },
  { kind: 'collection', slug: 'refrigerators-under-1000', label: 'Under $1,000' },
  { kind: 'collection', slug: 'refrigerators-under-2000', label: 'Under $2,000' },
  { kind: 'collection', slug: 'refrigerators-under-3000', label: 'Under $3,000' },
  { kind: 'hub', slug: 'luxury-refrigerators', label: 'Luxury $3,000+' },
]

/** Widths: no width collection exists in Supabase — search until the owner rules otherwise. */
const WIDTHS = [18, 21, 24, 27, 28, 30, 32, 33, 34, 35, 36]
export const WIDTH_CHIPS: HomeLink[] = [
  ...WIDTHS.map((w): HomeLink => ({ kind: 'search', slug: `${w} inch wide refrigerator`, label: `${w} in` })),
  { kind: 'hub', slug: 'counter-depth-refrigerators', label: 'Counter-Depth' },
  { kind: 'hub', slug: 'built-in-refrigerators', label: 'Built-In' },
]

/** Color navigation excludes panel-ready: it describes cabinet-panel configuration (FRIDGE-884). */
export const FRIDGE_COLORS: Array<HomeLink & { swatch: string }> = [
  { kind: 'collection', slug: 'stainless-steel-refrigerators', label: 'Stainless', swatch: 'linear-gradient(135deg, #f2f4f7 0%, #b9c0cb 45%, #e8ebf0 100%)' },
  { kind: 'collection', slug: 'white-refrigerators', label: 'White', swatch: 'linear-gradient(135deg, #ffffff, #eef1f5)' },
  { kind: 'collection', slug: 'black-refrigerators', label: 'Black', swatch: '#0c0d10' },
  { kind: 'collection', slug: 'silver-refrigerators', label: 'Silver', swatch: 'linear-gradient(135deg, #d4d7dc, #aab0b9)' },
  { kind: 'collection', slug: 'gray-refrigerators', label: 'Gray', swatch: 'linear-gradient(135deg, #9aa0a8, #7d838c)' },
  { kind: 'collection', slug: 'cream-refrigerators', label: 'Cream', swatch: 'linear-gradient(135deg, #fffdd0, #f2e8c9)' },
  { kind: 'collection', slug: 'slate-refrigerators', label: 'Slate', swatch: 'linear-gradient(135deg, #837b6e, #6e675c)' },
  { kind: 'collection', slug: 'black-stainless-refrigerators', label: 'Black Stainless', swatch: 'linear-gradient(135deg, #6a6b71, #565760)' },
  { kind: 'collection', slug: 'red-refrigerators', label: 'Red', swatch: '#d61318' },
  { kind: 'collection', slug: 'blue-refrigerators', label: 'Blue', swatch: '#8ed4e8' },
  { kind: 'collection', slug: 'green-refrigerators', label: 'Green', swatch: '#3f7d54' },
]

export const FREEZER_COLORS: Array<HomeLink & { swatch: string }> = [
  { kind: 'collection', slug: 'white-freezers', label: 'White', swatch: 'linear-gradient(135deg, #ffffff, #eef1f5)' },
  { kind: 'collection', slug: 'black-freezers', label: 'Black', swatch: '#0c0d10' },
  { kind: 'collection', slug: 'stainless-steel-freezers', label: 'Stainless', swatch: 'linear-gradient(135deg, #f2f4f7 0%, #b9c0cb 45%, #e8ebf0 100%)' },
  { kind: 'collection', slug: 'gray-freezers', label: 'Gray', swatch: 'linear-gradient(135deg, #9aa0a8, #7d838c)' },
  { kind: 'collection', slug: 'silver-freezers', label: 'Silver', swatch: 'linear-gradient(135deg, #d4d7dc, #aab0b9)' },
  { kind: 'collection', slug: 'red-freezers', label: 'Red', swatch: '#d61318' },
]

/** "Shop fridges and freezers by scenario" — 12 tiles. */
export const SCENARIO_TILES: HomeLink[] = [
  { kind: 'collection', slug: 'refrigerators-for-large-family', label: 'Families of 4+' },
  { kind: 'collection', slug: 'small-kitchen-refrigerators', label: 'Small Kitchens' },
  { kind: 'hub', slug: 'garage-refrigerators', label: 'Garage Ready' },
  { kind: 'collection', slug: 'dorm-mini-fridges', label: 'Dorm Rooms' },
  { kind: 'hub', slug: 'luxury-refrigerators', label: 'Luxury Kitchens' },
  { kind: 'hub', slug: 'commercial-refrigerators', label: 'Commercial Kitchens' },
  { kind: 'collection', slug: 'wine-fridges', label: 'Wine Collectors' },
  { kind: 'hub', slug: 'quiet-refrigerators', label: 'Quiet Appliances' },
  { kind: 'collection', slug: 'apartment-refrigerators', label: 'Rentals' },
  { kind: 'collection', slug: 'refrigerators-for-cabin', label: 'Cabin & Off-Grid' },
  { kind: 'hub', slug: 'outdoor-refrigerators', label: 'Outdoor & Garage' },
  { kind: 'collection', slug: 'refrigerators-under-1500', label: 'Budget Remodels' },
]

/** "Browse refrigeration by category" — 18 pills. */
export const BROWSE_PILLS: HomeLink[] = [
  { kind: 'hub', slug: 'best-refrigerators', label: 'Best Refrigerators', icon: 'award' },
  { kind: 'hub', slug: 'best-freezers', label: 'Best Freezers', icon: 'snow' },
  { kind: 'hub', slug: 'french-door-refrigerators', label: 'French Door', icon: 'home' },
  { kind: 'hub', slug: 'side-by-side-refrigerators', label: 'Side by Side', icon: 'compare' },
  { kind: 'hub', slug: 'mini-fridges', label: 'Mini Fridges', icon: 'min' },
  { kind: 'hub', slug: 'counter-depth-refrigerators', label: 'Counter Depth', icon: 'ruler' },
  { kind: 'hub', slug: 'energy-efficient-refrigerators', label: 'Energy Efficient', icon: 'leaf' },
  { kind: 'hub', slug: 'smart-fridges', label: 'Smart Fridges', icon: 'cpu' },
  { kind: 'hub', slug: 'large-capacity-refrigerators', label: 'Large Capacity', icon: 'max' },
  { kind: 'hub', slug: 'quiet-refrigerators', label: 'Quiet Fridges', icon: 'vol' },
  { kind: 'hub', slug: 'refrigerator-buying-guide', label: 'Buying Guide', icon: 'book' },
  { kind: 'hub', slug: 'freezer-buying-guide', label: 'Freezer Guide', icon: 'book' },
  { kind: 'hub', slug: 'energy-efficiency-guide', label: 'Energy Guide', icon: 'bolt' },
  { kind: 'hub', slug: 'wine-beverage-refrigerators', label: 'Wine & Beverage', icon: 'wine' },
  { kind: 'hub', slug: 'commercial-refrigerators', label: 'Commercial', icon: 'build' },
  { kind: 'route', slug: 'filters', label: 'Water Filters', icon: 'drop' },
]

/** Guides section — impression-ranked (same set the old homepage derived from GSC). */
export const GUIDE_SLUGS = [
  'best-refrigerators', 'best-freezers', 'refrigerator-sizing-guide', 'energy-efficiency-guide',
  'refrigerator-buying-guide', 'freezer-buying-guide', 'best-specialty-appliances', 'food-storage-appliances',
] as const

/** Free tools grid — 8 implemented tools. */
export const TOOL_SLUGS = [
  'refrigerator-size-calculator', 'energy-cost-calculator', 'find-your-fridge', 'kitchen-space-planner',
  'refrigerator-lifespan-calculator', 'refrigerator-recall-checker', 'refrigerator-repair-vs-replace-calculator',
  'refrigerator-warranty-comparison',
] as const

/** Shop by brand — 12 brands (brand hub when active, else the indexed brand collection). */
export const BRAND_SLUGS = [
  'samsung', 'lg', 'whirlpool', 'ge', 'hisense', 'frigidaire', 'bosch', 'kitchenaid', 'kenmore', 'amana', 'summit', 'danby',
] as const

/** Hero rotator and Price Intelligence — three real FINs with retailer ladders. */
export const HERO_FINS = ['FIN-0003863', 'FIN-0000110', 'FIN-0000108', 'FIN-0000161', 'FIN-0000068', 'FIN-0000082', 'FIN-0002740', 'FIN-0000107'] as const
export const PRICE_INTELLIGENCE_FIN = 'FIN-0000082'

/** Staff Picks — the owner-curated list. */
export const STAFF_PICK_FINS = ['FIN-0002740', 'FIN-0016877', 'FIN-0000947', 'FIN-0000534', 'FIN-0019023', 'FIN-0017306'] as const

/** The 13 most-populous states with a live /local/<state> page. */
export const LOCAL_STATES: Array<{ code: string; slug: string; name: string }> = [
  { code: 'CA', slug: 'california', name: 'California' }, { code: 'TX', slug: 'texas', name: 'Texas' },
  { code: 'FL', slug: 'florida', name: 'Florida' }, { code: 'NY', slug: 'new-york', name: 'New York' },
  { code: 'IL', slug: 'illinois', name: 'Illinois' }, { code: 'PA', slug: 'pennsylvania', name: 'Pennsylvania' },
  { code: 'OH', slug: 'ohio', name: 'Ohio' }, { code: 'GA', slug: 'georgia', name: 'Georgia' },
  { code: 'NC', slug: 'north-carolina', name: 'North Carolina' }, { code: 'MI', slug: 'michigan', name: 'Michigan' },
  { code: 'NJ', slug: 'new-jersey', name: 'New Jersey' }, { code: 'VA', slug: 'virginia', name: 'Virginia' },
  { code: 'WA', slug: 'washington', name: 'Washington' },
]

/**
 * Routes that exist in app/ (verified 09/02/2026) — the gate for kind: 'route'
 * AND for every one-segment literal href in the generated canvas markup
 * (resolveHref). A route missing here is rewritten to a search and logged as a
 * defect. 09/04/2026: + retailer-policies (FRIDGE-803) — measured first as
 * href="/search?q=retailer%20policies" on the Promise block.
 */
export const KNOWN_ROUTES = new Set([
  'about-us', 'brands', 'compare', 'contact', 'data', 'deals', 'faq', 'filters', 'financing', 'local', 'parts', 'press',
  'privacy-policy', 'rebates', 'recalls', 'recycling-guide', 'retailer-policies', 'search', 'sitemap', 'terms-of-service',
  'tools', 'warranty', 'widgets', 'saved',
])

export function searchHref(q: string): string {
  return `/search?q=${encodeURIComponent(q)}`
}

/** Apply the render-time gates. Returns null for anything that must not be linked. */
export function resolveLink(
  link: HomeLink,
  gates: { indexedCollections: ReadonlySet<string>; activeHubs: ReadonlySet<string> }
): ResolvedLink | null {
  switch (link.kind) {
    case 'collection':
      return gates.indexedCollections.has(link.slug) ? { ...link, href: `/collections/${link.slug}` } : null
    case 'hub':
      return gates.activeHubs.has(link.slug) ? { ...link, href: `/${link.slug}` } : null
    case 'route':
      return KNOWN_ROUTES.has(link.slug) ? { ...link, href: `/${link.slug}` } : null
    case 'search':
      return { ...link, href: searchHref(link.slug) }
  }
}

export function resolveLinks(
  links: readonly HomeLink[],
  gates: { indexedCollections: ReadonlySet<string>; activeHubs: ReadonlySet<string> }
): ResolvedLink[] {
  return links.map((l) => resolveLink(l, gates)).filter((l): l is ResolvedLink => l !== null)
}

const warned = new Set<string>()
/** Owner rule 09/02/2026: no virtual pages — a link to a page that does not exist is a defect to fix at the source, so it is logged once per process. */
function warnGated(href: string) {
  if (warned.has(href)) return
  warned.add(href)
  console.warn(`[homepage-2026] link ${href} is not a live page — its tile is not rendered until the page exists (run scripts/fridge735-build-homepage-link-collections.mjs)`)
}

/**
 * The gate for a literal href in the generated canvas markup: a collection renders
 * only if active + indexed, a hub only if active. Anything gated returns null and
 * the canvas anchor (`A`) renders nothing — the tile is dropped, exactly as
 * `resolveLink` drops a gated registry entry.
 *
 * FRIDGE-798: until 09/04/2026 a gated destination fell back to
 * `/search?q=<slug words>`. Measured on the served homepage that day: the
 * "Red" freezer swatch linked `/search?q=red%20freezers` (the collection exists
 * but holds 2 live red freezers, under the floor of 3), and that search page
 * serves `robots: noindex, follow`. Only the search box may search.
 */
export function resolveHref(href: string, gates: { indexedCollections: ReadonlySet<string>; activeHubs: ReadonlySet<string> }): string | null {
  if (!href.startsWith('/')) return href
  const path = href.split('?')[0].split('#')[0]
  const parts = path.split('/').filter(Boolean)
  if (parts[0] === 'collections' && parts.length === 2) {
    if (gates.indexedCollections.has(parts[1])) return href
    warnGated(href)
    return null
  }
  if (parts.length === 1 && !KNOWN_ROUTES.has(parts[0]) && !gates.activeHubs.has(parts[0])) {
    warnGated(href)
    return null
  }
  return href
}

/** Every collection and hub slug the registry names — the two gate queries read this. */
export function registrySlugs(): { collections: string[]; hubs: string[] } {
  const all: HomeLink[] = [
    ...HERO_CHIPS, ...CATEGORY_TILES, ...EXACT_NEED_PILLS, ...BUDGET_CHIPS, ...WIDTH_CHIPS,
    ...FRIDGE_COLORS, ...FREEZER_COLORS, ...SCENARIO_TILES, ...BROWSE_PILLS,
  ]
  const collections = new Set<string>(CANVAS_COLLECTION_SLUGS)
  const hubs = new Set<string>([...GUIDE_SLUGS, ...CANVAS_HUB_SLUGS])
  for (const l of all) {
    if (l.kind === 'collection') collections.add(l.slug)
    if (l.kind === 'hub') hubs.add(l.slug)
  }
  // section "Shop all" links and the specialty tabs
  for (const s of ['refrigerators', 'freezers', 'wine-fridges', 'mini-fridges', 'ice-makers', 'commercial', 'kegerators',
    'top-freezer-refrigerators', 'bottom-freezer-refrigerators', 'undercounter-refrigerators', 'undercounter-freezers',
    'deep-freezers', 'energy-star-refrigerators', 'commercial-display-cases', 'commercial-bar-coolers', 'all',
    'energy-star-appliances', 'beer-fridges', 'ice-makers'])
    collections.add(s)
  for (const h of ['garage-refrigerators', 'commercial-refrigerators', 'kegerators', 'ice-makers', 'wine-beverage-refrigerators',
    'mini-fridges', 'best-specialty-appliances', 'refrigerator-buying-guide'])
    hubs.add(h)
  return { collections: Array.from(collections), hubs: Array.from(hubs) }
}
